import {
  convertAnnouncementToDraft,
  createAnnouncement,
  getAnnouncementById,
  publishAnnouncement,
  updateAnnouncement,
} from '@/api/announcements';
import {
  AnnouncementStatus,
  SaveAnnouncementData,
} from '@/api/announcements/types';
import { Constants } from '@/constants/announcements';
import useErrorMessage from '@/hooks/useErrorMessage';
import useSnackbar from '@/hooks/useSnackbar';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { PreviewCard } from '../components/PreviewModal';
import { isRichTextEmpty } from '../components/RichTextEditor';
import {
  convertMockAnnouncementToDraft,
  createMockAnnouncement,
  getActiveMockAnnouncement,
  getMockAnnouncement,
  publishMockAnnouncement,
  updateMockAnnouncement,
  USE_MOCK,
} from '../mocks';

const MAX_CARDS = 5;

export interface EditableCard {
  key: number;
  id?: number;
  title: string;
  description: string;
  videoUrl: string;
  imageUrl: string | null;
}

interface UseAnnouncementFormHook {
  isEdit: boolean;
  status: AnnouncementStatus;
  name: string;
  cards: EditableCard[];
  openPublish: boolean;
  openConvert: boolean;
  openPreview: boolean;
  previewCards: PreviewCard[];
  activeWarning: string | undefined;
  showErrors: boolean;
  setName: (name: string) => void;
  setOpenPublish: (open: boolean) => void;
  setOpenConvert: (open: boolean) => void;
  setOpenPreview: (open: boolean) => void;
  handleAddCard: () => void;
  handleRemoveCard: (key: number) => void;
  handleCardChange: (
    key: number,
    field: 'title' | 'description' | 'videoUrl',
    value: string
  ) => void;
  handleImageUpload: (key: number, file: File) => void;
  handleRemoveImage: (key: number) => void;
  handleMoveCard: (fromIndex: number, toIndex: number) => void;
  handleSaveDraft: () => Promise<void>;
  handleConfirmConvert: () => Promise<void>;
  handleOpenPublish: () => void;
  handleConfirmPublish: () => Promise<void>;
  handleBack: () => void;
}

let nextCardKey = 1;

function emptyCard(): EditableCard {
  nextCardKey += 1;
  return {
    key: nextCardKey,
    title: '',
    description: '',
    videoUrl: '',
    imageUrl: null,
  };
}

export default function useAnnouncementForm(): UseAnnouncementFormHook {
  const [name, setName] = useState('');
  const [cards, setCards] = useState<EditableCard[]>([emptyCard()]);
  const [status, setStatus] = useState<AnnouncementStatus>('draft');
  const [openPublish, setOpenPublish] = useState(false);
  const [openConvert, setOpenConvert] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  const { id } = useParams();
  const announcementId = Number(id);
  const isEdit = Boolean(id);

  const { handleSnackbar } = useSnackbar();
  const { getErrorMessage } = useErrorMessage();
  const navigate = useNavigate();

  const loadAnnouncement = useCallback(async () => {
    if (!isEdit) return;
    try {
      if (USE_MOCK) {
        const mock = getMockAnnouncement(announcementId);
        if (!mock) throw new Error(Constants.notFound);
        setName(mock.title);
        setStatus(mock.status);
        setCards(
          [...mock.cards]
            .sort((a, b) => a.position - b.position)
            .map((card) => {
              nextCardKey += 1;
              return {
                key: nextCardKey,
                id: card.id,
                title: card.title,
                description: card.description,
                videoUrl: card.video_url ?? '',
                imageUrl: card.image_url,
              };
            })
        );
        return;
      }

      const response = await getAnnouncementById(announcementId);

      if (response.detail.description) {
        throw new Error(response.detail.description);
      }

      if (!response.data) {
        throw new Error('Algo deu errado, tente novamente.');
      }

      setName(response.data.title);
      setStatus(response.data.status);
      setCards(
        [...response.data.cards]
          .sort((a, b) => a.position - b.position)
          .map((card) => {
            nextCardKey += 1;
            return {
              key: nextCardKey,
              id: card.id,
              title: card.title,
              description: card.description,
              videoUrl: card.video_url ?? '',
              imageUrl: card.image_url,
            };
          })
      );
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
      navigate('/announcements');
    }
  }, [isEdit, announcementId, handleSnackbar, getErrorMessage, navigate]);

  useEffect(() => {
    loadAnnouncement();
  }, [loadAnnouncement]);

  const currentActive = USE_MOCK ? getActiveMockAnnouncement() : undefined;
  const activeWarning =
    currentActive && (!isEdit || currentActive.id !== announcementId)
      ? Constants.activeWillBeDeactivated(currentActive.title)
      : undefined;

  const handleAddCard = (): void => {
    if (cards.length >= MAX_CARDS) {
      handleSnackbar(Constants.maxCards, true);
      return;
    }
    setCards((prev) => [...prev, emptyCard()]);
  };

  const handleRemoveCard = (key: number): void => {
    setCards((prev) =>
      prev.length > 1 ? prev.filter((card) => card.key !== key) : prev
    );
  };

  const handleCardChange = (
    key: number,
    field: 'title' | 'description' | 'videoUrl',
    value: string
  ): void => {
    setCards((prev) =>
      prev.map((card) =>
        card.key === key ? { ...card, [field]: value } : card
      )
    );
  };

  const handleImageUpload = (key: number, file: File): void => {
    const imageUrl = URL.createObjectURL(file);
    setCards((prev) =>
      prev.map((card) =>
        card.key === key ? { ...card, imageUrl, videoUrl: '' } : card
      )
    );
  };

  const handleRemoveImage = (key: number): void => {
    setCards((prev) =>
      prev.map((card) =>
        card.key === key ? { ...card, imageUrl: null } : card
      )
    );
  };

  const handleMoveCard = (fromIndex: number, toIndex: number): void => {
    if (fromIndex === toIndex) return;
    setCards((prev) => {
      const updated = [...prev];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      return updated;
    });
  };

  const validateName = (): boolean => {
    if (!name.trim()) {
      setShowErrors(true);
      handleSnackbar(Constants.requiredName, true);
      return false;
    }

    setShowErrors(false);
    return true;
  };

  const validate = (): boolean => {
    if (!validateName()) return false;

    const hasInvalidCard = cards.some(
      (card) => !card.title.trim() || isRichTextEmpty(card.description)
    );

    if (hasInvalidCard) {
      setShowErrors(true);
      handleSnackbar(Constants.requiredCardFields, true);
      return false;
    }

    return true;
  };

  const toPayload = (): SaveAnnouncementData => ({
    title: name.trim(),
    cards: cards.map((card, index) => ({
      id: card.id,
      title: card.title.trim(),
      description: card.description.trim(),
      video_url: card.videoUrl.trim() || null,
      image_url: card.imageUrl,
      position: index + 1,
    })),
  });

  const saveAnnouncement = async (): Promise<number> => {
    const payload = toPayload();

    if (USE_MOCK) {
      if (isEdit) {
        updateMockAnnouncement(announcementId, payload);
        return announcementId;
      }
      return createMockAnnouncement(payload).id;
    }

    if (isEdit) {
      const response = await updateAnnouncement(announcementId, payload);
      if (response.detail.description) {
        throw new Error(response.detail.description);
      }
      return announcementId;
    }

    const response = await createAnnouncement(payload);
    if (response.detail.description) {
      throw new Error(response.detail.description);
    }
    if (!response.data) {
      throw new Error('Algo deu errado, tente novamente.');
    }
    return response.data.id;
  };

  const handleSaveDraft = async (): Promise<void> => {
    if (!validateName()) return;

    if (isEdit && status !== 'draft') {
      setOpenConvert(true);
      return;
    }

    try {
      await saveAnnouncement();
      handleSnackbar(Constants.savedAsDraft, false);
      navigate('/announcements');
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleConfirmConvert = async (): Promise<void> => {
    setOpenConvert(false);
    try {
      const savedId = await saveAnnouncement();
      if (USE_MOCK) {
        convertMockAnnouncementToDraft(savedId);
      } else {
        const response = await convertAnnouncementToDraft(savedId);
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
      }
      handleSnackbar(Constants.convertedToDraft, false);
      navigate('/announcements');
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleOpenPublish = (): void => {
    if (!validate()) return;
    setOpenPublish(true);
  };

  const handleConfirmPublish = async (): Promise<void> => {
    setOpenPublish(false);
    try {
      const savedId = await saveAnnouncement();
      if (USE_MOCK) {
        publishMockAnnouncement(savedId);
      } else {
        const response = await publishAnnouncement(savedId);
        if (response.detail.description) {
          throw new Error(response.detail.description);
        }
      }
      handleSnackbar(Constants.published, false);
      navigate('/announcements');
    } catch (error) {
      handleSnackbar(getErrorMessage(error), true);
    }
  };

  const handleBack = (): void => {
    navigate(-1);
  };

  const previewCards: PreviewCard[] = cards.map((card) => ({
    title: card.title,
    description: card.description,
    videoUrl: card.videoUrl.trim() || null,
    imageUrl: card.imageUrl,
  }));

  return {
    isEdit,
    status,
    name,
    cards,
    openPublish,
    openConvert,
    openPreview,
    previewCards,
    activeWarning,
    showErrors,
    setName,
    setOpenPublish,
    setOpenConvert,
    setOpenPreview,
    handleAddCard,
    handleRemoveCard,
    handleCardChange,
    handleImageUpload,
    handleRemoveImage,
    handleMoveCard,
    handleSaveDraft,
    handleConfirmConvert,
    handleOpenPublish,
    handleConfirmPublish,
    handleBack,
  };
}
